// Hub sidebar navigation — one config per top-level hub route so the sidebar
// transforms into a hub-scoped panel (BACK + items) the moment the user enters
// the hub, mirroring the client-workspace sidebar pattern.
//
// A hub is any top-level route that hosts a set of sub-tabs (Studio, Insights,
// Knowledge Hub, Compliance Reference). When AppLayout detects the current
// pathname matches one of these, it renders <HubNavPanel /> with the matching
// config in place of the global NAV_GROUPS.
import {
  ChartBar, Broadcast, TrendUp, Lightning, Dna,
  Database, BookOpenText, Stack, ListChecks,
  ShieldCheck, TreeStructure, Scales,
} from "@phosphor-icons/react";
import { ASSISTANTS } from "@/lib/assistants";

// Deep-link paths used by the Studio sidebar rail. Every assistant deep-link
// STAYS on /studio and only twiddles ?assistant= so the URL model (and back
// button) matches the existing AIStudio component contract.
const studioTo = (key) => `/studio?assistant=${key}`;

export const HUB_CONFIGS = {
  studio: {
    key: "studio",
    path: "/studio",
    title: "AI Studio",
    eyebrow: "Generate",
    sections: [{
      key: "assistants",
      label: "Assistants",
      // Every entry is derived from lib/assistants.js so this stays in sync
      // with whatever assistants the platform ships. The `perm` from the
      // catalog is copied through so HubNavPanel can hide items the user
      // can't access.
      items: ASSISTANTS.map((a) => ({
        key: a.key,
        label: a.label.replace(" Assistant", ""),
        icon: a.icon,
        to: studioTo(a.key),
        perm: a.perm,
        ai: true,
      })),
    }],
    // ?assistant=<key> is the source of truth for "which item is active" on Studio.
    activeItemKey: (_pathname, search) => new URLSearchParams(search).get("assistant") || null,
  },

  insights: {
    key: "insights",
    path: "/insights",
    title: "Insights",
    eyebrow: "Foresight",
    sections: [{
      key: "insights",
      label: "Sections",
      items: [
        { key: "analytics",  label: "Analytics",  icon: ChartBar,  to: "/insights?tab=analytics",  perm: "reports:view", ai: true },
        { key: "radar",      label: "Radar",      icon: Broadcast, to: "/insights?tab=radar",      perm: "reports:view", ai: true },
        { key: "forecast",   label: "Forecast",   icon: TrendUp,   to: "/insights?tab=forecast",   perm: "reports:view", ai: true },
        { key: "autonomous", label: "Autonomous", icon: Lightning, to: "/insights?tab=autonomous", perm: "reports:view", ai: true },
        { key: "firm-dna",   label: "Firm DNA",   icon: Dna,       to: "/insights?tab=firm-dna",   perm: "reports:view", ai: true },
      ],
    }],
    // Insights defaults ?tab= to "analytics" when unset — mirror that so the
    // first item is highlighted on the bare /insights entry-point.
    activeItemKey: (_pathname, search) => new URLSearchParams(search).get("tab") || "analytics",
  },

  knowledge: {
    key: "knowledge",
    path: "/knowledge",
    title: "Knowledge Hub",
    eyebrow: "Firm Library",
    sections: [{
      key: "library",
      label: "Sections",
      items: [
        { key: "knowledge", label: "Methodologies",    icon: Database,     to: "/knowledge?tab=knowledge", perm: "knowledge:view" },
        { key: "library",   label: "Content & Prior Art", icon: BookOpenText, to: "/knowledge?tab=library", perm: "dashboard:view" },
        { key: "master",    label: "Document Assembly",  icon: Stack,       to: "/knowledge?tab=master",   perm: "dashboard:view" },
        { key: "review",    label: "Review Queue",       icon: ListChecks,  to: "/knowledge?tab=review",   perm: "dashboard:view", badge: "output_approvals", badgeTone: "attention" },
      ],
    }],
    // Knowledge falls back to "knowledge" tab when ?tab= is missing.
    activeItemKey: (_pathname, search) => {
      const t = new URLSearchParams(search).get("tab");
      // Legacy aliases → merged tabs (matches KnowledgeHub.jsx TAB_ALIASES).
      if (t === "reuse" || t === "content") return "library";
      return t || "knowledge";
    },
  },

  "compliance-ref": {
    key: "compliance-ref",
    path: "/compliance-ref",
    title: "Compliance Reference",
    eyebrow: "DUCF Backbone",
    sections: [{
      key: "reference",
      label: "Reference",
      items: [
        { key: "controls",    label: "Controls",    icon: ShieldCheck,   to: "/compliance-ref?tab=controls",    perm: "dashboard:view" },
        { key: "frameworks",  label: "Frameworks",  icon: TreeStructure, to: "/compliance-ref?tab=frameworks",  perm: "dashboard:view" },
        { key: "regulations", label: "Regulations", icon: Scales,        to: "/compliance-ref?tab=regulations", perm: "dashboard:view" },
      ],
    }],
    activeItemKey: (_pathname, search) => new URLSearchParams(search).get("tab") || "controls",
  },
};

// Ordered list — longest path prefix first — so hubFromPath picks the most
// specific match if hubs ever nest.
const HUB_LIST = Object.values(HUB_CONFIGS).sort((a, b) => b.path.length - a.path.length);

/** Return the hub config whose path prefix matches `pathname`, else null. */
export function hubFromPath(pathname) {
  if (!pathname) return null;
  for (const hub of HUB_LIST) {
    // Match exact path OR path followed by "/" (avoid matching /studio-x).
    if (pathname === hub.path || pathname.startsWith(hub.path + "/")) return hub;
  }
  return null;
}

/** True when the current pathname is inside any hub. Used by AppLayout. */
export function isHubPath(pathname) {
  return hubFromPath(pathname) !== null;
}
